// src/App.jsx
/**
 * Global pDx entry application.
 *
 * Responsibility:
 *  - Provide the public pDx entry point.
 *  - Allow a user to select a supported region or enter a study/clinic code.
 *  - Route the user to the approved regional pDx frontend.
 *
 * Non-responsibility:
 *  - Authentication
 *  - Recording workflows
 *  - Questionnaires
 *  - Profile management
 *  - Results
 *  - Firebase reads/writes
 */

import React, { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import MDNavBar from "./components/navigation/MDNavBar";
import LegalDocumentPage from "./components/legal/LegalDocumentPage";
import NavBar from "./components/navigation/NavBar";
import { useNavVariant } from "./utils/navigation/useNavVariant";

const LandingPage = lazy(() => import("./pages/LandingPage"));

function LoadingFallback() {
  return (
    <div className="min-h-[100dvh] flex items-center justify-center px-6 text-center">
      <p className="text-sm text-slate-500">Loading pDx entry point…</p>
    </div>
  );
}

export default function App() {
  const navVariant = useNavVariant(); // 'main' | 'landing'

  return (
    <div className="h-[100dvh] flex flex-col typography text-pretty">
      {navVariant === "main" ? <NavBar /> : <MDNavBar />}
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/legal/terms"
            element={<LegalDocumentPage documentType="terms" />}
          />
          <Route
            path="/legal/privacy"
            element={<LegalDocumentPage documentType="privacy" />}
          />

          {/* Keep fallback deterministic for the global entry app */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}
